import React, { useState, useEffect } from 'react';
import { Instagram } from 'lucide-react';
import { API_URL } from '../../config';
import { INSTAGRAM_URL } from './socialLinks';

// ─── Types ────────────────────────────────────────────────────────────────────

interface InstagramPost {
  date_post: string;
  template_type: string | null;
  image_url: string;
  permalink: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** « lun. 11 août » — court, lisible sur une vignette. */
const formatPostDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Flux des dernières publications Instagram de clôture (@kaneabourse).
 *
 * Volontairement servi par notre propre API (`/public/social-posts`) plutôt que
 * par l'embed Meta : l'embed officiel ne sait afficher qu'un post isolé (pas un
 * profil), impose un script tiers lourd souvent bloqué, et ne se stylise pas.
 * Ici les images sont celles que nous générons déjà nous-mêmes.
 *
 * La grille est un bonus, jamais un point de rupture : tant qu'il n'y a rien à
 * montrer (API muette, images expirées, aucune publication récente), la section
 * se réduit à son invitation à suivre le compte — ce qui reste un rendu voulu.
 */
const InstagramFeed: React.FC = () => {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  // Une URL SAS peut expirer entre la mise en cache de l'API et l'affichage :
  // on retire la vignette morte plutôt que de laisser une image cassée.
  const [brokenUrls, setBrokenUrls] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/public/social-posts?limit=6`);
        if (!res.ok) throw new Error('API error');
        const json = await res.json();
        if (!cancelled) setPosts(Array.isArray(json.data) ? json.data : []);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPosts();
    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePosts = posts.filter((p) => !brokenUrls.includes(p.image_url));

  return (
    <section className="landing-instagram" id="instagram">
      <div className="instagram-inner">
        <div className="landing-section-label">Instagram</div>
        <h2 className="landing-section-title">Le marché BRVM, chaque soir</h2>
        <p className="landing-section-sub">
          À chaque séance, nous publions le résumé de la clôture sur Instagram : indices,
          plus fortes hausses, plus fortes baisses. Gratuit, sans inscription.
        </p>

        {loading ? (
          <div className="instagram-grid" aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="instagram-card instagram-card--skeleton" />
            ))}
          </div>
        ) : visiblePosts.length > 0 ? (
          <div className="instagram-grid">
            {visiblePosts.map((post) => (
              <a
                key={post.permalink}
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
                className="instagram-card"
                aria-label={`Publication du ${formatPostDate(post.date_post)} sur Instagram`}
              >
                <img
                  src={post.image_url}
                  alt={`Résumé de la séance BRVM du ${formatPostDate(post.date_post)}`}
                  loading="lazy"
                  onError={() =>
                    setBrokenUrls((prev) =>
                      prev.includes(post.image_url) ? prev : [...prev, post.image_url]
                    )
                  }
                />
                <span className="instagram-card__date">{formatPostDate(post.date_post)}</span>
              </a>
            ))}
          </div>
        ) : null}

        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="instagram-follow-btn"
        >
          <Instagram size={18} />
          Suivre @kaneabourse
        </a>
      </div>
    </section>
  );
};

export default InstagramFeed;
